import { Component, Input, OnChanges, OnInit, SimpleChange, SimpleChanges } from '@angular/core';
import { ActivatedRoute, Router, RouterLink, RouterLinkActive, RouterOutlet } from '@angular/router';
import { CommonModule } from '@angular/common';

import { TOOLTIP_TEXTS } from '../../../../utils/tooltip-texts';
import { Tooltip } from '../../../../utils/Tooltip';
import { TooltipContent } from '../../../../utils/TooltipContent';
import { CapitalizePipe } from '../../../../pipes/capitalize.pipe';
import { ARBleListsService, ARBleListType } from '../../../../services/agrorendement-ble-lists.service';
import { ListItem } from '../../../../models/lists.model';
import { ARAgrofertData } from '../../../../models/agrorendement-common.model';
import {
  ARBleAzoteFrequency,
  ARBleAzotePeriod,
  ARBleAzoteProductType,
  ARBleCIDestructionPeriod,
  ARBleCIDevelopment,
  ARBleCIType,
  ARBleEtatVegetatifSh,
  ARBlePastResiduType,
  ARBlePrairieAge,
  ARBlePrairieDestructionPeriod,
  ARBlePrairieExploitationMode,
  ARBlePrairiePostDestructionRank,
  ARBlePrairieType,
} from '../../../../models/agrorendement-ble-lists.model';
import { isBooleanValue, showErrorMessage, sortingNumber, sortingString, toBoolean } from '../../../../utils/utils';
import { ModalService } from '../../../../services/modal.service';
import { TranslatePipe, TranslateWithPunctuationPipe } from '../../../../pipes/translate.pipe';
import { FormsModule } from '@angular/forms';
import { TooltipModule } from 'ngx-bootstrap/tooltip';

@Component({
  standalone: true,
  selector: 'ar-agrofert-edit',
  templateUrl: 'agrofert-edit.component.html',
  imports: [RouterLink, RouterLinkActive, RouterOutlet, CommonModule, Tooltip, TooltipContent, CapitalizePipe, TranslatePipe, TranslateWithPunctuationPipe, FormsModule, TooltipModule],
})
export class ARAgrofertEditComponent implements OnInit, OnChanges {
  @Input() agrofertData: ARAgrofertData;
  @Input() lowCost: boolean;
  @Input() isBatch: boolean;
  @Input() canPPF: boolean;
  @Input() cbBatchEdit: Object;
  error: any;

  areListsFetched = false;
  azoteProductTypes: ARBleAzoteProductType[];
  azotePeriods: ARBleAzotePeriod[];
  etatsVegetatifsSh: ARBleEtatVegetatifSh[];
  pastAzoteFrequencies: ARBleAzoteFrequency[];
  pastAzoteProductTypes: ARBleAzoteProductType[];
  pastAzotePreviousCultureProductTypes: ARBleAzoteProductType[];
  pastResiduTypes: ARBlePastResiduType[];
  prairieAges: ARBlePrairieAge[];
  prairieDestructionPeriods: ARBlePrairieDestructionPeriod[];
  prairiePostDestructionRanks: ARBlePrairiePostDestructionRank[];
  prairieExploitationModes: ARBlePrairieExploitationMode[];
  prairieTypes: ARBlePrairieType[];
  ciTypes: ARBleCIType[];
  ciDevelopments: ARBleCIDevelopment[];
  ciDestructionPeriods: ARBleCIDestructionPeriod[];

  productTypesIndex = new Map<number, string>();

  requireAgrofertInfo: boolean = undefined;
  requireAgrofertReliquatSh = false;
  requireAgrofertFirstAzoteInformation = false;
  requireAgrofertFirstAzoteConcentration = false;
  requireAgrofertSecondAzote = false;
  requireAgrofertSecondAzoteInformation = false;
  requireAgrofertSecondAzoteConcentration = false;
  requireAgrofertReliquatSortieHiver = false;
  requireAgrofertPastAzoteProductType = false;
  requireAgrofertPastAzotePreviousCultureChoice = false;
  requireAgrofertPastAzotePreviousCulture = false;
  requireAgrofertPastAzotePreviousCultureConcentration = false;
  requireAgrofertPrairie = false;
  requireAgrofertCI = false;

  tooltipTexts: any;

  constructor(
    private bleListService: ARBleListsService,
    private modal: ModalService,
    private router: Router,
    private route: ActivatedRoute,
  ) {
    this.tooltipTexts = TOOLTIP_TEXTS;
  }

  getLists() {
    const listTypes = [
      ARBleListType.BLE_AZOTE_PRODUCT_TYPES,
      ARBleListType.BLE_AZOTE_FREQUENCIES,
      ARBleListType.BLE_ETATS_VEGETATIFS_SH,
      ARBleListType.BLE_AZOTE_PERIODS,
      ARBleListType.BLE_PAST_RESIDU_TYPES,
      ARBleListType.BLE_PRAIRIE_AGES,
      ARBleListType.BLE_PRAIRIE_DESTRUCTION_PERIODS,
      ARBleListType.BLE_PRAIRIE_POST_DESTRUCTION_RANKS,
      ARBleListType.BLE_PRAIRIE_EXPLOITATION_MODES,
      ARBleListType.BLE_PRAIRIE_TYPES,
      ARBleListType.BLE_CI_TYPES,
      ARBleListType.BLE_CI_DEVELOPMENTS,
      ARBleListType.BLE_CI_DESTRUCTION_PERIODS,
    ];
    return this.bleListService.getListTypes(ARBleListType, ...listTypes);
  }

  handleLists(lists: Map<ARBleListType, ListItem[]>) {
    // TODO meileur facon de gerer ca ?
    this.areListsFetched = true;

    this.azoteProductTypes = lists.get(ARBleListType.BLE_AZOTE_PRODUCT_TYPES).sort(sortingString('label'));
    for (const p of this.azoteProductTypes) {
      this.productTypesIndex.set(p.id, p.code);
    }
    this.pastAzoteProductTypes = this.azoteProductTypes.filter((p) => p.code !== 'OTHER' && p.code !== 'NONE');
    this.pastAzotePreviousCultureProductTypes = this.azoteProductTypes.filter((p) => p.code !== 'NONE');
    // on connait l'ordre d'insert dans la base de donnees : on garde cet ordre
    this.azotePeriods = lists.get(ARBleListType.BLE_AZOTE_PERIODS).sort(sortingNumber('id'));
    this.pastAzoteFrequencies = lists.get(ARBleListType.BLE_AZOTE_FREQUENCIES).sort(sortingNumber('id'));
    this.etatsVegetatifsSh = lists.get(ARBleListType.BLE_ETATS_VEGETATIFS_SH).sort(sortingNumber('id'));
    this.pastResiduTypes = lists.get(ARBleListType.BLE_PAST_RESIDU_TYPES).sort(sortingNumber('id'));
    this.prairieAges = lists.get(ARBleListType.BLE_PRAIRIE_AGES).sort(sortingNumber('id'));
    this.prairieDestructionPeriods = lists.get(ARBleListType.BLE_PRAIRIE_DESTRUCTION_PERIODS).sort(sortingNumber('id'));
    this.prairiePostDestructionRanks = lists
      .get(ARBleListType.BLE_PRAIRIE_POST_DESTRUCTION_RANKS)
      .sort(sortingNumber('id'));
    this.prairieExploitationModes = lists.get(ARBleListType.BLE_PRAIRIE_EXPLOITATION_MODES).sort(sortingNumber('id'));
    this.prairieTypes = lists.get(ARBleListType.BLE_PRAIRIE_TYPES).sort(sortingString('label'));
    this.ciTypes = lists.get(ARBleListType.BLE_CI_TYPES).sort(sortingString('label'));
    this.ciDevelopments = lists.get(ARBleListType.BLE_CI_DEVELOPMENTS).sort(sortingNumber('id'));
    this.ciDestructionPeriods = lists.get(ARBleListType.BLE_CI_DESTRUCTION_PERIODS).sort(sortingNumber('id'));
  }

  ngOnInit() {
    const obs2 = this.getLists();
    obs2.subscribe(
      (data: Map<ARBleListType, ListItem[]>) => {
        // on suppose que le orderItem a ete
        this.handleLists(data);
        // au cas ou le orderItem ait ete sette avant
        if (this.agrofertData) {
          this.prepare();
        }
      },
      (e) => this.handleGetError(e),
    );
  }

  ngOnChanges(changes: SimpleChanges) {
    const agroferDataChange: SimpleChange = changes['agrofertData'];
    const islowcostChange: SimpleChange = changes['lowCost'];
    // au cas ou les listes ait ete recuperees avant
    if (agroferDataChange !== undefined && agroferDataChange.isFirstChange() && this.areListsFetched) {
      this.prepare();
    }

    if (islowcostChange !== undefined) {
      if (this.lowCost == true) {
        this.agrofertData.needsAgrofert = false;
        this.onChangeNeedsAgrofert(false);
      }
    }
  }

  prepare() {
    if (!isBooleanValue(this.agrofertData.needsAgrofert)) {
      this.agrofertData.needsAgrofert = false;
    }
    this.onChangeNeedsAgrofert(this.agrofertData.needsAgrofert, true);
  }

  onChangeNeedsAgrofert(needsAgrofert: any, isInit = false) {
    needsAgrofert = toBoolean(needsAgrofert);
    this.requireAgrofertInfo = needsAgrofert;
    if (!needsAgrofert) {
      // TODO efface les donnees du calcul agrofert sauf celles necessaires au risque de verse ?!
      // this.prepareNewAgrofertData();
      if (isInit) {
        this.prepareAgrofertData();
      }
    } else {
      this.agrofertData.userProvidedAzote = null;
      if (isInit) {
        this.prepareAgrofertData();
      }
    }
  }

  prepareNewAgrofertData(agrofertData: ARAgrofertData) {
    this.agrofertData.etatVegetatifSh = new ARBleEtatVegetatifSh();
    this.agrofertData.etatVegetatifSh.id = null;

    this.agrofertData.hasReliquatSh = null;
    this.agrofertData.reliquatSh = null;

    this.agrofertData.firstAzoteProductType = new ARBleAzoteProductType();
    this.agrofertData.firstAzoteProductType.id = null;

    this.agrofertData.firstAzotePeriod = new ARBleAzotePeriod();
    this.agrofertData.firstAzotePeriod.id = null;

    this.agrofertData.firstAzoteConcentration = null;
    this.agrofertData.firstAzoteDensity = null;

    this.agrofertData.secondAzoteProductType = new ARBleAzoteProductType();
    this.agrofertData.secondAzoteProductType.id = null;

    this.agrofertData.secondAzotePeriod = new ARBleAzotePeriod();
    this.agrofertData.secondAzotePeriod.id = null;

    this.agrofertData.secondAzoteConcentration = null;
    this.agrofertData.secondAzoteDensity = null;

    this.agrofertData.pastAzoteProductType = new ARBleAzoteProductType();
    this.agrofertData.pastAzoteProductType.id = null;

    this.agrofertData.pastAzoteFrequency = new ARBleAzoteFrequency();
    this.agrofertData.pastAzoteFrequency.id = null;

    this.agrofertData.hasPastAzotePreviousCulture = null;
    this.agrofertData.pastAzotePreviousCultureProductType = new ARBleAzoteProductType();
    this.agrofertData.pastAzotePreviousCultureProductType.id = null;

    this.agrofertData.pastAzotePreviousCultureConcentration = null;
    this.agrofertData.pastAzotePreviousCultureDensity = null;

    this.agrofertData.pastResiduType = new ARBlePastResiduType();
    this.agrofertData.pastResiduType.id = null;

    this.agrofertData.hasPrairie = null;
    this.agrofertData.prairieAge = new ARBlePrairieAge();
    this.agrofertData.prairieAge.id = null;

    this.agrofertData.prairieDestructionPeriod = new ARBlePrairieDestructionPeriod();
    this.agrofertData.prairieDestructionPeriod.id = null;

    this.agrofertData.prairiePostDestructionRank = new ARBlePrairiePostDestructionRank();
    this.agrofertData.prairiePostDestructionRank.id = null;

    this.agrofertData.prairieExploitationMode = new ARBlePrairieExploitationMode();
    this.agrofertData.prairieExploitationMode.id = null;

    this.agrofertData.prairieType = new ARBlePrairieType();
    this.agrofertData.prairieType.id = null;

    this.agrofertData.hasCi = null;

    this.agrofertData.ciType = new ARBleCIType();
    this.agrofertData.ciType.id = null;

    this.agrofertData.ciDevelopment = new ARBleCIDevelopment();
    this.agrofertData.ciDevelopment.id = null;

    this.agrofertData.ciDestructionPeriod = new ARBleCIDestructionPeriod();
    this.agrofertData.ciDestructionPeriod.id = null;

    this.agrofertData.isCiEvery2Years = null;
  }

  prepareAgrofertData() {
    if (!isBooleanValue(this.agrofertData.needsAgrofert)) {
      this.agrofertData.needsAgrofert = null;
    } else {
      this.onChangeNeedsAgrofert(this.agrofertData.needsAgrofert);
    }

    if (!this.agrofertData.etatVegetatifSh) {
      this.agrofertData.etatVegetatifSh = new ARBleEtatVegetatifSh();
      // TODO mettre les id= null dans le constructeur de ListItem
      this.agrofertData.etatVegetatifSh.id = null;
    }

    let changeHasReliquatSh = false;
    if (!isBooleanValue(this.agrofertData.hasReliquatSh)) {
      this.agrofertData.hasReliquatSh = null;
    } else {
      changeHasReliquatSh = true;
    }

    let changeFirstAzoteProductType = false;
    if (!this.agrofertData.firstAzoteProductType) {
      this.agrofertData.firstAzoteProductType = new ARBleAzoteProductType();
      this.agrofertData.firstAzoteProductType.id = null;
    } else {
      changeFirstAzoteProductType = true;
    }

    if (!this.agrofertData.firstAzotePeriod) {
      this.agrofertData.firstAzotePeriod = new ARBleAzotePeriod();
      this.agrofertData.firstAzotePeriod.id = null;
    }

    let changeSecondAzoteProductType = false;
    if (!this.agrofertData.secondAzoteProductType) {
      this.agrofertData.secondAzoteProductType = new ARBleAzoteProductType();
      this.agrofertData.secondAzoteProductType.id = null;
    } else {
      changeSecondAzoteProductType = true;
    }

    if (!this.agrofertData.secondAzotePeriod) {
      this.agrofertData.secondAzotePeriod = new ARBleAzotePeriod();
      this.agrofertData.secondAzotePeriod.id = null;
    }

    let changePastAzoteFrequency = false;
    if (!this.agrofertData.pastAzoteFrequency) {
      this.agrofertData.pastAzoteFrequency = new ARBleAzoteFrequency();
      this.agrofertData.pastAzoteFrequency.id = null;
    } else {
      changePastAzoteFrequency = true;
    }

    if (!this.agrofertData.pastAzoteProductType) {
      this.agrofertData.pastAzoteProductType = new ARBleAzoteProductType();
      this.agrofertData.pastAzoteProductType.id = null;
    }

    let changePastAzotePreviousCulture = false;
    if (!isBooleanValue(this.agrofertData.hasPastAzotePreviousCulture)) {
      this.agrofertData.hasPastAzotePreviousCulture = null;
    } else {
      changePastAzotePreviousCulture = true;
    }

    let changePastAzotePreviousCultureProductType = false;
    if (!this.agrofertData.pastAzotePreviousCultureProductType) {
      this.agrofertData.pastAzotePreviousCultureProductType = new ARBleAzoteProductType();
      this.agrofertData.pastAzotePreviousCultureProductType.id = null;
    } else {
      changePastAzotePreviousCultureProductType = true;
    }

    if (!this.agrofertData.pastResiduType) {
      this.agrofertData.pastResiduType = new ARBlePastResiduType();
      this.agrofertData.pastResiduType.id = null;
    }

    let changeHasPrairie = false;
    if (!isBooleanValue(this.agrofertData.hasPrairie)) {
      this.agrofertData.hasPrairie = null;
    } else {
      changeHasPrairie = true;
    }

    if (!this.agrofertData.prairieAge) {
      this.agrofertData.prairieAge = new ARBlePrairieAge();
      this.agrofertData.prairieAge.id = null;
    }

    if (!this.agrofertData.prairieDestructionPeriod) {
      this.agrofertData.prairieDestructionPeriod = new ARBlePrairieDestructionPeriod();
      this.agrofertData.prairieDestructionPeriod.id = null;
    }

    if (!this.agrofertData.prairiePostDestructionRank) {
      this.agrofertData.prairiePostDestructionRank = new ARBlePrairiePostDestructionRank();
      this.agrofertData.prairiePostDestructionRank.id = null;
    }

    if (!this.agrofertData.prairieExploitationMode) {
      this.agrofertData.prairieExploitationMode = new ARBlePrairieExploitationMode();
      this.agrofertData.prairieExploitationMode.id = null;
    }

    if (!this.agrofertData.prairieType) {
      this.agrofertData.prairieType = new ARBlePrairieType();
      this.agrofertData.prairieType.id = null;
    }

    let changeHasCI = false;
    if (!isBooleanValue(this.agrofertData.hasCi)) {
      this.agrofertData.hasCi = null;
    } else {
      changeHasCI = true;
    }

    if (!this.agrofertData.ciType) {
      this.agrofertData.ciType = new ARBleCIType();
      this.agrofertData.ciType.id = null;
    }

    if (!this.agrofertData.ciDevelopment) {
      this.agrofertData.ciDevelopment = new ARBleCIDevelopment();
      this.agrofertData.ciDevelopment.id = null;
    }

    if (!this.agrofertData.ciDestructionPeriod) {
      this.agrofertData.ciDestructionPeriod = new ARBleCIDestructionPeriod();
      this.agrofertData.ciDestructionPeriod.id = null;
    }

    if (!isBooleanValue(this.agrofertData.isCiEvery2Years)) {
      this.agrofertData.isCiEvery2Years = null;
    }

    // on fait a la fin pour que tout soit initialise
    if (changeHasReliquatSh) {
      this.onChangeHasReliquatSh(this.agrofertData.hasReliquatSh);
    }

    if (changeFirstAzoteProductType) {
      this.onChangeFirstAzoteProductType(this.agrofertData.firstAzoteProductType.id);
    }

    if (changeSecondAzoteProductType) {
      this.onChangeSecondAzoteProductType(this.agrofertData.secondAzoteProductType.id);
    }

    if (changePastAzoteFrequency) {
      this.onChangePastAzoteFrequency(this.agrofertData.pastAzoteFrequency.id);
    }

    if (changePastAzotePreviousCulture) {
      this.onChangePastAzotePreviousCulture(this.agrofertData.hasPastAzotePreviousCulture);
    }

    if (changePastAzotePreviousCultureProductType) {
      this.onChangePastAzotePreviousCultureProductType(this.agrofertData.pastAzotePreviousCultureProductType.id);
    }

    if (changeHasPrairie) {
      this.onChangeHasPrairie(this.agrofertData.hasPrairie);
    }

    if (changeHasCI) {
      this.onChangeHasCI(this.agrofertData.hasCi);
    }
  }

  onChangeFirstAzoteProductType(productTypeId: any) {
    if (!productTypeId) {
      return;
    }
    productTypeId = +productTypeId;
    const code = this.productTypesIndex.get(productTypeId);

    if (code === 'OTHER') {
      this.requireAgrofertFirstAzoteConcentration = true;
    } else {
      this.requireAgrofertFirstAzoteConcentration = false;
    }

    const noneProductType = this.azoteProductTypes.filter((productType) => productType.code === 'NONE')[0];
    if (code === 'NONE') {
      this.agrofertData.firstAzoteConcentration = undefined;
      this.agrofertData.firstAzoteDensity = undefined;

      this.agrofertData.firstAzotePeriod.id = null;

      // si pas de premier apport => pas de second apport non plus
      // on le vide
      this.agrofertData.secondAzoteProductType.id = null;
      this.agrofertData.secondAzotePeriod.id = null;
      this.agrofertData.secondAzoteConcentration = undefined;
      this.agrofertData.secondAzoteDensity = undefined;

      // on n'affiche pas la periode ni le second apport
      this.requireAgrofertFirstAzoteInformation = false;
      this.requireAgrofertSecondAzote = false;
    } else {
      this.requireAgrofertFirstAzoteInformation = true;
      this.requireAgrofertSecondAzote = true;
    }
  }

  onChangeSecondAzoteProductType(productTypeId: any) {
    if (!productTypeId) {
      return;
    }
    productTypeId = +productTypeId;
    const code = this.productTypesIndex.get(productTypeId);
    if (code === 'OTHER') {
      this.requireAgrofertSecondAzoteConcentration = true;
    } else {
      this.requireAgrofertSecondAzoteConcentration = false;
    }

    if (code === 'NONE') {
      this.agrofertData.secondAzoteConcentration = undefined;
      this.agrofertData.secondAzoteDensity = undefined;
      this.agrofertData.secondAzotePeriod.id = null;

      this.requireAgrofertSecondAzoteInformation = false;
    } else {
      this.requireAgrofertSecondAzoteInformation = true;
    }
  }

  onChangePastAzoteFrequency(azoteFrequencyId: any) {
    if (!azoteFrequencyId) {
      return;
    }
    azoteFrequencyId = +azoteFrequencyId;
    const azoteFrequency = this.pastAzoteFrequencies.filter((f) => f.id === azoteFrequencyId)[0];
    if (azoteFrequency.code === 'NONE') {
      // pas besoin du produit
      this.requireAgrofertPastAzoteProductType = false;
      this.requireAgrofertPastAzotePreviousCultureChoice = false;
      this.agrofertData.pastAzoteProductType.id = null;

      // pas besoin du choix culture precedente
      this.agrofertData.hasPastAzotePreviousCulture = null;
      this.onChangePastAzotePreviousCulture(false);
    } else {
      this.requireAgrofertPastAzoteProductType = true;

      this.requireAgrofertPastAzotePreviousCultureChoice = azoteFrequency.code !== 'TLA';
      if (!this.requireAgrofertPastAzotePreviousCultureChoice) {
        this.agrofertData.hasPastAzotePreviousCulture = null;
        this.onChangePastAzotePreviousCulture(false);
      }
    }
  }

  onChangePastAzotePreviousCulture(hasPastAzotePreviousCulture: any) {
    hasPastAzotePreviousCulture = toBoolean(hasPastAzotePreviousCulture);

    this.requireAgrofertPastAzotePreviousCulture = hasPastAzotePreviousCulture;
    if (!hasPastAzotePreviousCulture) {
      this.agrofertData.pastAzotePreviousCultureProductType.id = null;
      this.agrofertData.pastAzotePreviousCultureConcentration = undefined;
      this.agrofertData.pastAzotePreviousCultureDensity = undefined;
    }
  }

  onChangePastAzotePreviousCultureProductType(productTypeId: any) {
    if (!productTypeId) {
      return;
    }
    productTypeId = +productTypeId;
    const code = this.productTypesIndex.get(productTypeId);

    if (code === 'OTHER') {
      this.requireAgrofertPastAzotePreviousCultureConcentration = true;
    } else {
      this.requireAgrofertPastAzotePreviousCultureConcentration = false;
    }
  }

  onChangeHasReliquatSh(hasReliquat: any) {
    hasReliquat = toBoolean(hasReliquat);

    this.requireAgrofertReliquatSh = hasReliquat;
    if (!hasReliquat) {
      this.agrofertData.reliquatSh = undefined;
    }
  }

  onChangeHasPrairie(hasPrairie: any) {
    hasPrairie = toBoolean(hasPrairie);
    this.requireAgrofertPrairie = hasPrairie;
    if (!hasPrairie) {
      this.agrofertData.prairieAge.id = null;
      this.agrofertData.prairieDestructionPeriod.id = null;
      this.agrofertData.prairiePostDestructionRank.id = null;
      this.agrofertData.prairieExploitationMode.id = null;
      this.agrofertData.prairieType.id = null;
    }
  }

  onChangeHasCI(hasCI: any) {
    hasCI = toBoolean(hasCI);
    this.requireAgrofertCI = hasCI;
    if (!hasCI) {
      this.agrofertData.ciType.id = null;
      this.agrofertData.ciDevelopment.id = null;
      this.agrofertData.ciDestructionPeriod.id = null;
      this.agrofertData.isCiEvery2Years = null;
    }
  }

  handleGetError(error: any) {
    if (error.status === 401) {
      showErrorMessage(this.modal, "Vous n'etes pas autorise a consulter cette parcelle").then((r) => {
        this.router.navigate(['/orders', 'azote', 'orge', 'parcels']);
      });
    } else {
      console.error(error);
      showErrorMessage(this.modal, 'Une erreur est survenue').then((r) => {
        this.router.navigate(['/orders', 'azote', 'orge', 'parcels']);
      });
    }
  }
}
